import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { path } from 'ramda'
import { useToast } from '@machobear/design-system'
import { useDispatch, useSelector } from 'react-redux'

import { colors } from '@/styles'
import { useEnsureLoggedIn } from '@/hooks/use-ensure-loggedIn'
import useRole from '@/hooks/use-role'
import useSessionComplete from '@/hooks/use-session-complete'
import { useDSLogin } from '@/features/Auth/Context/ds-login-provider'
import { HeartIconShadow, HeartSimpleIcon } from '@/components/icons'

import { useTermsDialogContext } from '../terms-dialog/context'

const Wrapper = styled.button`
  cursor: pointer;
  padding: 0;
  background: transparent;

  svg {
    flex-shrink: 0;
  }

  ${props =>
    !props.noShadow &&
    `
    svg {
      filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.2));
    }
  `}

  ${props =>
    props.iconColor &&
    `
    svg {
        path {
          stroke: ${props.offColor ? props.offColor : props.iconColor};
          stroke-width: 1.5;
        }
      }
    `}


  &:hover {
    svg {
      path {
        fill: rgba(255, 255, 255, 0.1);
      }
    }
  }

  ${props =>
    props.selected &&
    `
    svg {
      path {
        fill: ${props.iconColor ? props.iconColor : colors.white};
        stroke: ${props.iconColor ? props.iconColor : props.white};
        stroke-width: 1.5;
      }
    }

    &:hover {
      opacity: 0.9;
      svg {
        path {
          fill: ${props.iconColor ? props.iconColor : colors.white};
        }
      }
    }
  `}
`

const FavouriteButton = ({
  listingId,
  children,
  iconColor,
  offColor,
  noShadow,
  eventId,
  isCardLocked = false,
  showDSLoginModal,
  listingCardDlv,
  ...rest
}) => {
  const dispatch = useDispatch()
  const role = useRole()
  const { t } = useTranslation()
  const isLoggedIn = useSessionComplete()
  const isLandlord = role === 'landlord'
  const favourites = useSelector(path(['listings', 'favourites']))
  const isFavourite = listingId in favourites
  const showToast = useToast()
  const { showModalForRequiredTerms } = useTermsDialogContext()
  const { setAfterLogin } = useDSLogin()
  const ensureLoggedIn = useEnsureLoggedIn({ showLogin: showDSLoginModal })

  const onFavourite = async () => {
    if (isFavourite) {
      const response = await dispatch.listings.removeFavourite(listingId)
      if (response?.error)
        showToast({ message: response.error.message, tone: 'critical' })
      else
        showToast({
          message: t(
            'favourites:listing_removed',
            'Listing removed from favourites!'
          ),
          action: {
            label: t('favourites:view_favourites', 'View your favourites'),
            onClick: () => {
              window.open(`/favourites`, '_blank')
            }
          },
          tone: 'positive'
        })
    } else {
      const response = await dispatch.listings.favourite(listingId)

      if (response?.error)
        showToast({ message: response.error.message, tone: 'critical' })
      else
        showToast({
          message: t(
            'favourites:listing_saved',
            'Listing saved to favourites!'
          ),
          action: {
            label: t('favourites:view_favourites', 'View your favourites'),
            onClick: () => {
              window.open(`/favourites`, '_blank')
            }
          },
          tone: 'positive'
        })
    }
  }

  const handleClickForUnlockedListing = () => {
    if (isLoggedIn) {
      onFavourite()
    } else {
      setAfterLogin(onFavourite)
      showDSLoginModal()
    }
  }

  const handleClick = e => {
    e.preventDefault()
    e.stopPropagation()
    if (eventId) {
      window.dataLayer.push({
        event: eventId,
        listingCard: listingCardDlv
      })
    }

    isCardLocked
      ? ensureLoggedIn(() =>
          showModalForRequiredTerms('trreb_agreements', onFavourite)
        )
      : handleClickForUnlockedListing()
  }

  return !isLandlord ? (
    <>
      <Wrapper
        selected={isFavourite}
        onClick={handleClick}
        iconColor={iconColor}
        offColor={offColor}
        noShadow={noShadow}
        {...rest}
      >
        {noShadow ? (
          <HeartSimpleIcon originalsize="true" />
        ) : (
          <HeartIconShadow originalsize="true" />
        )}
        {children}
      </Wrapper>
    </>
  ) : null
}

export default FavouriteButton
